import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  Timestamp,
  orderBy,
  QuerySnapshot,
  DocumentData
} from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

// Types
export interface Feedback {
  id?: string;
  userId: string;
  userName: string;
  userEmail: string;
  message: string;
  rating: number;
  category: 'bug' | 'feature' | 'improvement' | 'other';
  status: 'new' | 'inProgress' | 'resolved' | 'closed';
  adminResponse?: string;
  createdAt: Date;
  updatedAt: Date;
  isResolved: boolean;
}

interface FeedbackState {
  feedback: Feedback[];
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
}

type FeedbackAction =
  | { type: 'SET_FEEDBACK'; payload: Feedback[] }
  | { type: 'ADD_FEEDBACK'; payload: Feedback }
  | { type: 'UPDATE_FEEDBACK'; payload: Feedback }
  | { type: 'DELETE_FEEDBACK'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ADMIN'; payload: boolean };

interface FeedbackContextType {
  state: FeedbackState;
  createFeedback: (feedback: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'isResolved'>) => Promise<void>;
  updateFeedback: (feedback: Feedback) => Promise<void>;
  deleteFeedback: (feedbackId: string) => Promise<void>;
  respondToFeedback: (feedbackId: string, response: string) => Promise<void>;
  getUserFeedback: () => Promise<void>;
  getAllFeedback: () => Promise<void>;
  checkIsAdmin: () => Promise<boolean>;
}

const initialState: FeedbackState = {
  feedback: [],
  loading: false,
  error: null,
  isAdmin: false,
};

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

function feedbackReducer(state: FeedbackState, action: FeedbackAction): FeedbackState {
  switch (action.type) {
    case 'SET_FEEDBACK':
      return { ...state, feedback: action.payload, loading: false };
    case 'ADD_FEEDBACK':
      return { ...state, feedback: [...state.feedback, action.payload], loading: false };
    case 'UPDATE_FEEDBACK':
      return {
        ...state,
        feedback: state.feedback.map(f =>
          f.id === action.payload.id ? action.payload : f
        ),
        loading: false,
      };
    case 'DELETE_FEEDBACK':
      return {
        ...state,
        feedback: state.feedback.filter(f => f.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_ADMIN':
      return { ...state, isAdmin: action.payload };
    default:
      return state;
  }
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(feedbackReducer, initialState);

  const checkIsAdmin = useCallback(async () => {
    const user = auth.currentUser;
    if (!user?.phoneNumber) return false;
    
    const isAdmin = ['+998991963315', '+998901234568'].includes(user.phoneNumber);
    dispatch({ type: 'SET_ADMIN', payload: isAdmin });
    return isAdmin;
  }, []);

  const createFeedback = useCallback(async (feedbackData: Omit<Feedback, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'isResolved'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentDate = new Date();
      const newFeedback: Omit<Feedback, 'id'> = {
        ...feedbackData,
        status: 'new',
        isResolved: false,
        createdAt: currentDate,
        updatedAt: currentDate,
      };

      const docRef = await addDoc(collection(db, 'feedback'), {
        ...newFeedback,
        createdAt: Timestamp.fromDate(currentDate),
        updatedAt: Timestamp.fromDate(currentDate),
      });

      dispatch({
        type: 'ADD_FEEDBACK',
        payload: { ...newFeedback, id: docRef.id },
      });
    } catch (error) {
      console.error('Error creating feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create feedback' });
    }
  }, []);

  const updateFeedback = useCallback(async (feedback: Feedback) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const { id, ...updateData } = feedback;
      if (!id) throw new Error('Feedback ID is required for update');

      const feedbackRef = doc(db, 'feedback', id);
      await updateDoc(feedbackRef, {
        ...updateData,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      dispatch({ type: 'UPDATE_FEEDBACK', payload: feedback });
    } catch (error) {
      console.error('Error updating feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update feedback' });
    }
  }, [checkIsAdmin]);

  const deleteFeedback = useCallback(async (feedbackId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      await deleteDoc(doc(db, 'feedback', feedbackId));
      dispatch({ type: 'DELETE_FEEDBACK', payload: feedbackId });
    } catch (error) {
      console.error('Error deleting feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete feedback' });
    }
  }, [checkIsAdmin]);

  const respondToFeedback = useCallback(async (feedbackId: string, response: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const feedbackRef = doc(db, 'feedback', feedbackId);
      await updateDoc(feedbackRef, {
        adminResponse: response,
        status: 'resolved',
        isResolved: true,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      const updatedFeedback = state.feedback.find(f => f.id === feedbackId);
      if (updatedFeedback) {
        dispatch({
          type: 'UPDATE_FEEDBACK',
          payload: {
            ...updatedFeedback,
            adminResponse: response,
            status: 'resolved',
            isResolved: true,
            updatedAt: new Date(),
          },
        });
      }
    } catch (error) {
      console.error('Error responding to feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to respond to feedback' });
    }
  }, [checkIsAdmin, state.feedback]);

  const getUserFeedback = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const q = query(
        collection(db, 'feedback'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const feedbackList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      })) as Feedback[];

      dispatch({ type: 'SET_FEEDBACK', payload: feedbackList });
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch feedback' });
    }
  }, []);

  const getAllFeedback = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const isAdmin = await checkIsAdmin();
      if (!isAdmin) throw new Error('Unauthorized access');

      const q = query(
        collection(db, 'feedback'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const feedbackList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      })) as Feedback[];

      dispatch({ type: 'SET_FEEDBACK', payload: feedbackList });
    } catch (error) {
      console.error('Error fetching all feedback:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch feedback' });
    }
  }, [checkIsAdmin]);

  const value = {
    state,
    createFeedback,
    updateFeedback,
    deleteFeedback,
    respondToFeedback,
    getUserFeedback,
    getAllFeedback,
    checkIsAdmin,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}